import {gql} from '@apollo/client';

export const CREATE_ASSET_CLASS = gql`
  mutation CreateAssetClass($input: AssetClassInput!) {
    createAssetClass(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAssetClass {
        message
      }
    }
  }
`;

export const DELETE_ASSET_CLASS = gql`
  mutation DeleteAssetClass($deleteAssetClassId: ID!) {
    deleteAssetClass(id: $deleteAssetClassId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAssetClass {
        message
      }
    }
  }
`;

export const TRANSLATE_ASSET_CLASS = gql`
  mutation TranslateAssetClass(
    $translateAssetClassId: ID!
    $language: LanguageEnum!
  ) {
    translateAssetClass(id: $translateAssetClassId, language: $language) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAssetClass {
        message
      }
    }
  }
`;

export const UPDATE_ASSET_CLASS = gql`
  mutation UpdateAssetClass(
    $updateAssetClassId: ID!
    $input: AssetClassInput!
  ) {
    updateAssetClass(id: $updateAssetClassId, input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAssetClass {
        message
      }
    }
  }
`;

export const UPDATE_RATING = gql`
  mutation UpdateRating($updateRatingId: ID!, $input: RatingInput!) {
    updateRating(id: $updateRatingId, input: $input) {
      ... on ResponseWithRating {
        message
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;

export const CREATE_RATING = gql`
  mutation CreateRating($input: RatingInput!) {
    createRating(input: $input) {
      ... on ResponseWithRating {
        message
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const CREATE_BOND = gql`
  mutation CreateBond($input: BondInput!) {
    createBond(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const CREATE_MUTUAL_FUND = gql`
  mutation CreateMutualFund($input: MutualFundInput!) {
    createMutualFund(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

/**
 * One translate mutation for every instrument type.
 *
 * The migration collapsed translateBond / translateMutualFund into
 * `translateFinancialInstrument`; neither of the old names exists on the
 * gateway any more, which is why Confirm Translation was throwing
 * "Cannot query field translateMutualFund on type Mutation".
 *
 * It also covers IPOs and public equities, which had no translate path when
 * their per-type mutations were absent.
 */
export const TRANSLATE_FINANCIAL_INSTRUMENT = gql`
  mutation TranslateFinancialInstrument($id: ID!, $language: LanguageEnum!) {
    translateFinancialInstrument(id: $id, language: $language) {
      ... on Error {
        statusCode
        message
      }
      ... on Response {
        message
      }
    }
  }
`;

/**
 * Publish a translation to users in that language, accepting the copy as written.
 *
 * The only other way to clear PENDING_REVIEW is to open the language in the editor and save,
 * which forces an admin to modify copy they were happy with — and for IPOs, public equities
 * and private equities there is no per-language editor at all, so this is the ONLY way their
 * translations ever reach mobile.
 *
 * Idempotent server-side: approving an already-approved row answers plainly rather than
 * erroring, so a double click is not a failure.
 */
export const APPROVE_INSTRUMENT_TRANSLATION = gql`
  mutation ApproveInstrumentTranslation($id: ID!, $language: LanguageEnum!) {
    approveInstrumentTranslation(id: $id, language: $language) {
      ... on Error {
        statusCode
        message
      }
      ... on Response {
        message
      }
    }
  }
`;

export const DELETE_FINANCIAL_INSTRUMENT = gql`
  mutation DeleteFinancialInstrument($deleteFinancialInstrumentId: ID!) {
    deleteFinancialInstrument(id: $deleteFinancialInstrumentId) {
      ... on Error {
        statusCode
        message
      }
      ... on Response {
        message
      }
    }
  }
`;

export const UPDATE_BOND = gql`
  mutation UpdateBond(
    $updateBondId: ID!
    $language: LanguageEnum!
    $input: UpdateBondInput!
  ) {
    updateBond(id: $updateBondId, language: $language, input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const UPDATE_FINANCIAL_INSTRUMENT_STATUS = gql`
  mutation UpdateFinancialInstrumentStatus(
    $updateFinancialInstrumentStatusId: ID
    $status: AssetStatus
  ) {
    updateFinancialInstrumentStatus(
      id: $updateFinancialInstrumentStatusId
      status: $status
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on Response {
        message
      }
    }
  }
`;

export const GROUP_FINANCIAL_INSTRUMENTS = gql`
  mutation GroupFinancialInstruments($input: FinancialInstrumentGroupInput!) {
    groupFinancialInstruments(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const DELETE_GROUPED_FINANCIAL_INSTRUMENT = gql`
  mutation UngroupFinancialInstruments($input: FinancialInstrumentGroupInput!) {
    ungroupFinancialInstruments(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on Response {
        message
      }
    }
  }
`;

export const UPDATE_MUTUAL_FUND = gql`
  mutation UpdateMutualFund(
    $updateMutualFundId: ID!
    $language: LanguageEnum!
    $input: UpdateMutualFundInput!
  ) {
    updateMutualFund(
      id: $updateMutualFundId
      language: $language
      input: $input
    ) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const UPDATE_INSTRUMENT_VALUATION = gql`
  mutation UpdateMutualFundCompanyValuationHistory(
    $input: mutualFundValuationHistoryInput!
  ) {
    updateMutualFundCompanyValuationHistory(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithMutualFundCompanyValuation {
        message
      }
    }
  }
`;

export const UPDATE_INSTRUMENT_INVENTORY = gql`
  mutation ConfigureInventoryPosition(
    $configureInventoryPositionId: ID!
    $input: UpdateInventoryInput!
  ) {
    configureInventoryPosition(
      id: $configureInventoryPositionId
      input: $input
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInstrumentInventoryPosition {
        message
      }
    }
  }
`;

export const CREATE_INVENTORY_MOVEMENT = gql`
  mutation CreateInventoryMovement(
    $input: InventoryMovementInput!
    $instrumentId: ID!
    $instrumentKey: InstrumentKey!
  ) {
    createInventoryMovement(
      input: $input
      instrumentId: $instrumentId
      instrumentKey: $instrumentKey
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInstrumentInventoryPosition {
        message
      }
    }
  }
`;

// The gateway argument is `matured`, not `isMatured`.
export const UPDATE_BOND_MATURITY_STATUS = gql`
  mutation UpdateBondMaturityStatus($instrumentId: ID!, $matured: Boolean!) {
    updateBondMaturityStatus(instrumentId: $instrumentId, matured: $matured) {
      ... on Error {
        message
        statusCode
      }
      ... on Response {
        message
      }
    }
  }
`;

export const RUN_MANAGED_FUND_INVENTORY_PREDICTION = gql`
  mutation RunManagedFundInventoryPrediction(
    $triggeredBy: String
    $email: String
  ) {
    runManagedFundInventoryPrediction(
      triggeredBy: $triggeredBy
      email: $email
    ) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithInventoryPredictionRun {
        message
        data {
          runId
          status
        }
      }
    }
  }
`;

export const BULK_UPSERT_MANAGED_FUND_INVENTORY_CONFIGS = gql`
  mutation BulkUpsertManagedFundInventoryConfigs(
    $input: BulkUpsertManagedFundInventoryConfigInput!
  ) {
    bulkUpsertManagedFundInventoryConfigs(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithManagedFundInventoryConfigs {
        message
        data {
          id
          ticker
          bufferRate
          weeklyDivisor
          active
          dabaName
          assetManager
        }
      }
    }
  }
`;

/* ---------------------------------------------------------------------------
   IPOs and public equities.

   IPO updates target the language open in the editor. Creates carry their
   source language in the base input. Both types can be translated through
   TRANSLATE_FINANCIAL_INSTRUMENT.
   --------------------------------------------------------------------------- */

export const CREATE_IPO = gql`
  mutation CreateIpo($input: IpoInput!) {
    createIpo(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const UPDATE_IPO = gql`
  mutation UpdateIpo(
    $updateIpoId: ID!
    $input: UpdateIpoInput!
    $language: LanguageEnum
  ) {
    updateIpo(id: $updateIpoId, input: $input, language: $language) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const CREATE_PUBLIC_EQUITY = gql`
  mutation CreatePublicEquity($input: PublicEquityInput!) {
    createPublicEquity(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const UPDATE_PUBLIC_EQUITY = gql`
  mutation UpdatePublicEquity(
    $updatePublicEquityId: ID!
    $input: UpdatePublicEquityInput!
  ) {
    updatePublicEquity(id: $updatePublicEquityId, input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

// Graduates an allocated IPO into a tradeable PublicEquity, setting
// Ipo.listedInstrumentId and the new equity's sourceIpoId. Without this an
// IPO never becomes a stock (§14c).
export const LIST_IPO = gql`
  mutation ListIpo($instrumentId: ID!) {
    listIpo(instrumentId: $instrumentId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;

export const MARK_INSTRUMENT_AS_LISTED = gql`
  mutation MarkInstrumentAsListed($instrumentId: ID!) {
    markInstrumentAsListed(instrumentId: $instrumentId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        message
      }
    }
  }
`;
